import { useEffect, useRef, useState } from "react";

import { Icon } from "../components/Icon.jsx";
import { prepareDriverCameraEvidence } from "./driver-camera-pipeline.js";
import { notifyDriver } from "./driver-notifications.js";
import {
  storePendingDriverMedia,
  supersedeDriverMedia,
  syncPendingDriverMedia,
} from "./offline-store.js";

export function DriverCameraField({
  label,
  mediaKind,
  metadata = {},
  onChange,
  clearAfterUse = false,
  disabled = false,
}) {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const mountedRef = useRef(true);
  const [selected, setSelected] = useState(null);
  const [accepted, setAccepted] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [preparingLabel, setPreparingLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [resultState, setResultState] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => () => {
    if (selected?.previewUrl) URL.revokeObjectURL(selected.previewUrl);
  }, [selected?.previewUrl]);

  useEffect(() => () => {
    if (accepted?.previewUrl) URL.revokeObjectURL(accepted.previewUrl);
  }, [accepted?.previewUrl]);

  useEffect(() => {
    setPreviewOpen(false);
  }, [selected?.previewUrl, accepted?.previewUrl]);

  useEffect(() => {
    if (!previewOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setPreviewOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [previewOpen]);

  function openCamera() {
    if (preparing || saving || disabled) return;
    cameraInputRef.current?.click();
  }

  function openGallery() {
    if (preparing || saving || disabled) return;
    galleryInputRef.current?.click();
  }

  async function selectFile(event, sourceType) {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    setPreparing(true);
    setPreparingLabel("Menambahkan waktu, lokasi, alamat, dan peta...");
    setError("");
    try {
      const prepared = await prepareDriverCameraEvidence(file, {
        sourceType,
        label,
        mediaKind,
        metadata,
      });
      const previewUrl = URL.createObjectURL(prepared.file);
      setSelected((current) => {
        if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
        return {
          ...prepared,
          previewUrl,
          originalName: file.name || "Foto kamera",
        };
      });
    } catch (selectionError) {
      const message = selectionError.message || "Foto belum dapat diproses.";
      setError(message);
      notifyDriver(message, { tone: "error", title: "Foto belum dapat dipakai" });
    } finally {
      setPreparing(false);
      setPreparingLabel("");
    }
  }

  function cancelSelection() {
    setSelected(null);
    setError("");
  }

  async function removeAcceptedPhoto() {
    if (!accepted || saving) return;
    if (!window.confirm(`Batalkan ${label.toLowerCase()} yang sudah dipilih?`)) return;
    setSaving(true);
    try {
      await supersedeDriverMedia(accepted.localId).catch(() => {});
      await onChange?.("", { removed: true, localId: accepted.localId });
      setAccepted(null);
      setResultState("");
      notifyDriver("Foto dibatalkan dari formulir.", { tone: "info" });
    } catch (removeError) {
      setError(removeError.message || "Foto belum dapat dibatalkan.");
    } finally {
      setSaving(false);
    }
  }

  async function usePhoto() {
    if (!selected || saving) return;
    setSaving(true);
    setError("");
    try {
      const local = await storePendingDriverMedia(selected.file, mediaKind, {
        ...metadata,
        width: selected.width,
        height: selected.height,
        originalFile: selected.originalFile,
        originalWidth: selected.originalWidth,
        originalHeight: selected.originalHeight,
        stampMetadata: selected.stampMetadata,
      });
      try {
        await onChange?.(local.id, {
          localId: local.id,
          serverMediaId: null,
          file: selected.file,
          width: selected.width,
          height: selected.height,
          byteSize: selected.file.size,
          stampMetadata: selected.stampMetadata,
          status: "PENDING",
        });
      } catch (changeError) {
        await supersedeDriverMedia(local.id).catch(() => {});
        throw changeError;
      }

      if (accepted?.localId && accepted.localId !== local.id) {
        await supersedeDriverMedia(accepted.localId).catch(() => {});
      }

      setResultState("Foto aman dan menunggu sinkronisasi");
      if (clearAfterUse) {
        setSelected(null);
      } else {
        const acceptedPreviewUrl = URL.createObjectURL(selected.file);
        setAccepted({
          ...selected,
          localId: local.id,
          previewUrl: acceptedPreviewUrl,
          status: "PENDING",
        });
        setSelected(null);
      }

      if (navigator.onLine) {
        void syncPendingDriverMedia({ onlyId: local.id, force: true, includeRecoverable: true })
          .then((sync) => {
            if (!mountedRef.current) return;
            const synced = Boolean(sync.mappings.get(local.id));
            setResultState(synced ? "Foto tersinkronisasi" : "Foto aman dan akan dicoba lagi");
            setAccepted((current) => current?.localId === local.id
              ? { ...current, status: synced ? "SYNCED" : "PENDING" }
              : current);
          })
          .catch(() => {
            if (mountedRef.current) setResultState("Foto aman dan akan dicoba lagi");
          });
      }
    } catch (saveError) {
      const message = saveError.message || "Foto belum dapat disimpan.";
      setError(message);
      notifyDriver(message, { tone: "error", title: "Foto belum tersimpan" });
    } finally {
      setSaving(false);
    }
  }

  const resultSynced = resultState === "Foto tersinkronisasi";
  const resultLabel = resultSynced
    ? "Tersinkron"
    : resultState.includes("menunggu")
      ? "Tersimpan lokal"
      : resultState.includes("dicoba lagi")
        ? "Menunggu ulang"
        : resultState;

  const activePreview = selected || accepted || null;
  const previewMetadata = activePreview?.stampMetadata || null;
  const previewAccuracy = Number(previewMetadata?.gpsAccuracyM);
  const hasPreviewLocation = Number.isFinite(Number(previewMetadata?.latitude))
    && Number.isFinite(Number(previewMetadata?.longitude));
  const previewMapResolved = previewMetadata?.mapSnapshotStatus === "RESOLVED";
  const accuracyText = Number.isFinite(previewAccuracy)
    ? `±${Math.max(1, Math.round(previewAccuracy))} m`
    : "akurasi tidak tersedia";
  const locationQuality = !hasPreviewLocation
    ? "Lokasi tidak tersedia"
    : !Number.isFinite(previewAccuracy)
      ? "Lokasi ditemukan"
      : previewAccuracy <= 15
        ? "Lokasi akurat"
        : previewAccuracy <= 30
          ? "Lokasi cukup"
          : "Lokasi kurang akurat";
  const integrityText = previewMetadata
    ? `${locationQuality}${hasPreviewLocation ? ` · ${accuracyText}` : ""} · ${previewMapResolved ? "Peta tersedia" : "Peta tidak tersedia"}`
    : "";

  return (
    <section className={`driver-camera-field ${accepted && !selected ? "is-accepted" : ""}`.trim()}>
      <div className="driver-camera-field__heading">
        <span><Icon name="camera" size={18} /> {label}</span>
        {resultState && (
          <small aria-label={resultState} className={`${resultSynced ? "is-success" : "is-pending"} driver-camera-sync-badge`} title={resultState}>
            <Icon name={resultSynced ? "check" : "clock"} size={14} /> <span>{resultLabel}</span>
          </small>
        )}
      </div>

      <input
        accept="image/*"
        aria-label={`Ambil ${label.toLowerCase()} dengan kamera`}
        capture="environment"
        className="driver-visually-hidden-input"
        disabled={disabled || preparing || saving}
        onChange={(event) => selectFile(event, "CAMERA")}
        ref={cameraInputRef}
        type="file"
      />
      <input
        accept="image/*"
        aria-label={`Pilih ${label.toLowerCase()} dari galeri`}
        className="driver-visually-hidden-input"
        disabled={disabled || preparing || saving}
        onChange={(event) => selectFile(event, "GALLERY")}
        ref={galleryInputRef}
        type="file"
      />

      {preparing && (
        <div className="driver-camera-preparing" role="status">
          <span className="loading-spinner" />
          <span><strong>Memproses foto</strong><small>{preparingLabel}</small></span>
        </div>
      )}

      {selected ? (
        <div className="driver-camera-preview">
          <button
            aria-label={`Perbesar pratinjau ${label.toLowerCase()}`}
            className="driver-camera-preview__frame"
            onClick={() => setPreviewOpen(true)}
            type="button"
          >
            <img alt={`Pratinjau ${label} dengan timestamp`} src={selected.previewUrl} />
          </button>
          <small className="driver-camera-preview__caption">
            {Math.max(1, Math.round(selected.file.size / 1024))} KB · Timestamp Sedulur Dalan · Ketuk foto untuk memperbesar
          </small>
          <div>
            <button className="driver-camera-secondary" disabled={saving} onClick={openCamera} type="button">
              <Icon name="camera" size={17} /> Ambil ulang
            </button>
            <button className="driver-secondary-button" disabled={saving} onClick={cancelSelection} type="button">Batal</button>
            <button className="driver-camera-primary" disabled={saving} onClick={usePhoto} type="button">
              <Icon name="check" size={17} /> {saving ? "Menyimpan..." : accepted ? "Gunakan foto pengganti" : "Gunakan foto"}
            </button>
          </div>
        </div>
      ) : accepted && !clearAfterUse ? (
        <div className="driver-camera-preview driver-camera-preview--accepted">
          <button
            aria-label={`Perbesar ${label.toLowerCase()} bertimestamp`}
            className="driver-camera-preview__frame"
            onClick={() => setPreviewOpen(true)}
            type="button"
          >
            <img alt={`${label} bertimestamp yang digunakan`} src={accepted.previewUrl} />
          </button>
          <div className="driver-camera-accepted-note">
            <span><Icon name="check" size={16} /> Foto digunakan · bertimestamp</span>
            <small>{Math.max(1, Math.round(accepted.file.size / 1024))} KB</small>
          </div>
          <div className="driver-camera-accepted-actions">
            <button className="driver-camera-secondary" disabled={saving} onClick={openCamera} type="button">
              <Icon name="camera" size={17} /> Ganti foto
            </button>
            <button className="driver-secondary-button" disabled={saving} onClick={removeAcceptedPhoto} type="button">
              <Icon name="close" size={16} /> Batalkan foto
            </button>
          </div>
        </div>
      ) : !preparing ? (
        <div className="driver-camera-field__choices">
          <button className="driver-camera-primary" disabled={disabled || preparing || saving} onClick={openCamera} type="button">
            <Icon name="camera" size={19} /> Ambil foto
          </button>
          <button className="driver-camera-secondary" disabled={disabled || preparing || saving} onClick={openGallery} type="button">
            <Icon name="upload" size={18} /> Pilih galeri
          </button>
          <small>Semua foto otomatis diberi waktu, identitas driver, lokasi GPS, alamat, dan mini map. Foto dari galeri juga diberi penanda sumber.</small>
        </div>
      ) : null}

      {previewOpen && activePreview?.previewUrl && (
        <div
          aria-label={`Pratinjau penuh ${label}`}
          aria-modal="true"
          className="driver-camera-lightbox"
          onClick={() => setPreviewOpen(false)}
          role="dialog"
        >
          <button
            aria-label="Tutup pratinjau penuh"
            className="driver-camera-lightbox__close"
            onClick={() => setPreviewOpen(false)}
            type="button"
          >
            <Icon name="close" size={24} />
          </button>
          <div className="driver-camera-lightbox__content" onClick={(event) => event.stopPropagation()}>
            <img alt={`${label} bertimestamp ukuran penuh`} src={activePreview.previewUrl} />
            <small>Timestamp Sedulur Dalan · ketuk area gelap atau tombol tutup untuk kembali</small>
          </div>
        </div>
      )}

      {previewMetadata?.locationWarning && (
        <small className="driver-camera-location-warning"><Icon name="warning" size={14} /> {previewMetadata.locationWarning}</small>
      )}
      {previewMetadata && (
        <small className={`driver-camera-integrity-note ${(!hasPreviewLocation || !previewMapResolved || (Number.isFinite(previewAccuracy) && previewAccuracy > 30)) ? "is-warning" : ""}`.trim()}>
          <Icon name="location" size={14} /> {integrityText}
        </small>
      )}
      {error && <small className="driver-field-error" role="alert">{error}</small>}
    </section>
  );
}
